/**
 * Field Trip Tags Page
 * Generates printable field trip name tags for children
 * Tags are sized 2.25" x 3.5" to fit standard badge holders
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Printer, RotateCcw, Users, CheckSquare, Square, Edit3 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Border colors for tags - cute and playful
const BORDER_COLORS = [
  "#E57373", // Coral/Red
  "#FFD54F", // Yellow
  "#4DB6AC", // Teal
  "#F48FB1", // Pink
  "#81C784", // Green
  "#FFB74D", // Orange
  "#64B5F6", // Blue
  "#BA68C8", // Purple
];

// Rainbow colors for "FIELD TRIP" text
const RAINBOW_COLORS = ["#E57373", "#FFB74D", "#FFD54F", "#81C784", "#64B5F6", "#7986CB", "#BA68C8", "#F48FB1", "#E57373", "#FFB74D"];

const FieldTripTagsPage = () => {
  const { user, organization } = useAuth();
  const { language } = useLanguage();
  const printRef = useRef();
  
  const [children, setChildren] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [customTeacher, setCustomTeacher] = useState(""); // For manual input
  const [useCustomTeacher, setUseCustomTeacher] = useState(false); // Toggle between dropdown and manual
  const [orientation, setOrientation] = useState("portrait"); // portrait or landscape
  const [loading, setLoading] = useState(true);
  const [childTeachers, setChildTeachers] = useState({}); // Track teacher per child

  // Fetch children and staff
  useEffect(() => {
    fetchData();
  }, []);

  // Set default teacher when user/organization loads
  useEffect(() => {
    if (user?.name) {
      setSelectedTeacher(user.name);
    } else if (organization?.ownerName) {
      setSelectedTeacher(organization.ownerName);
    }
  }, [user, organization]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [childrenRes, staffRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/staff-records`)
      ]);
      
      // Filter active children only
      const activeChildren = (childrenRes.data || []).filter(c => !c.deleted);
      setChildren(activeChildren);
      
      // Extract staff names
      const staff = staffRes.data || [];
      setStaffMembers(staff.map(s => s.staffName || s.name).filter(Boolean));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from children
  const categories = [...new Set(children.map(c => c.fullTimeSubcategory || c.category).filter(Boolean))];

  // Filter children by category
  const filteredChildren = selectedCategory === "all" 
    ? children 
    : children.filter(c => (c.fullTimeSubcategory || c.category) === selectedCategory);

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedChildren.length === filteredChildren.length) {
      setSelectedChildren([]);
    } else {
      setSelectedChildren(filteredChildren.map(c => c.id || c._id));
    }
  };

  // Handle individual child selection
  const handleChildSelect = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  // Apply teacher to all selected children
  const handleApplyTeacherToAll = () => {
    const newTeachers = {};
    selectedChildren.forEach(childId => {
      newTeachers[childId] = useCustomTeacher ? customTeacher : selectedTeacher;
    });
    setChildTeachers(prev => ({ ...prev, ...newTeachers }));
  };

  // Get the active teacher name (either from dropdown or custom input)
  const getActiveTeacher = () => {
    if (useCustomTeacher && customTeacher.trim()) {
      return customTeacher.trim();
    }
    return selectedTeacher || user?.name || organization?.ownerName || "Teacher";
  };

  // Get teacher for a specific child
  const getTeacherForChild = (childId) => {
    return childTeachers[childId] || getActiveTeacher();
  };

  // Get selected children data
  const selectedChildrenData = children.filter(c => selectedChildren.includes(c.id || c._id));

  // Calculate tags per page based on orientation
  const getTagsPerPage = () => {
    return orientation === "portrait" ? 8 : 6; // 4x2 portrait, 3x2 landscape
  };

  // Format category name for display
  const formatCategory = (cat) => {
    if (!cat) return "";
    return cat.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    const cols = orientation === 'portrait' ? 4 : 3;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Field Trip Tags</title>
          <style>
            @page {
              size: ${orientation === 'portrait' ? '8.5in 11in' : '11in 8.5in'};
              margin: 0.25in;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: 'Comic Sans MS', 'Chalkboard', 'Arial Rounded MT Bold', sans-serif;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .tags-container {
              display: grid;
              grid-template-columns: repeat(${cols}, 2.25in);
              grid-auto-rows: 3.5in;
              gap: 0;
              justify-content: center;
              padding: 0.1in;
            }
            .tag-wrapper {
              width: 2.25in;
              height: 3.5in;
              position: relative;
              page-break-inside: avoid;
            }
            /* Perforation cut lines - dashed borders */
            .tag-wrapper::after {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              width: 0;
              border-right: 1px dashed #999;
            }
            .tag-wrapper::before {
              content: '';
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              height: 0;
              border-bottom: 1px dashed #999;
            }
            /* Remove right border on last column */
            .tag-wrapper:nth-child(${cols}n)::after {
              border-right: none;
            }
            .tag {
              width: 100%;
              height: 100%;
              border-radius: 12px;
              padding: 8px;
              display: flex;
              flex-direction: column;
              align-items: center;
              background: white;
              position: relative;
            }
            .tag-border {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              border-radius: 12px;
              border: 4px solid;
              pointer-events: none;
            }
            .tag-inner {
              position: relative;
              z-index: 1;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .hole-punch {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 3px solid #888;
              margin-bottom: 4px;
              background: white;
            }
            .logo {
              width: 40px;
              height: auto;
              margin-bottom: 2px;
            }
            .header-text {
              font-size: 11px;
              color: #333;
              margin-bottom: 2px;
            }
            .field-trip-text {
              font-size: 16px;
              font-weight: bold;
              letter-spacing: 1px;
              margin-bottom: 6px;
            }
            .field-trip-text span {
              display: inline;
            }
            .field-label {
              font-size: 9px;
              font-weight: bold;
              color: #333;
              margin-top: 4px;
              text-transform: uppercase;
            }
            .field-value {
              font-size: 11px;
              color: #222;
              border-bottom: 1px solid #999;
              padding-bottom: 2px;
              margin-bottom: 2px;
              min-width: 80%;
              text-align: center;
            }
            .school-section {
              margin-top: auto;
              padding-top: 4px;
              width: 100%;
              text-align: center;
            }
            /* Scissors icon indicator */
            .cut-guide {
              text-align: center;
              color: #999;
              font-size: 10px;
              margin-bottom: 5px;
              border-bottom: 1px dashed #999;
              padding-bottom: 3px;
            }
            @media print {
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="cut-guide">✂ - - - Cut along dashed lines - - - ✂</div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // === Excursion Info Sheets — one detailed page per selected child ===
  const handlePrintExcursionSheets = () => {
    const esc = (s) => {
      if (s === null || s === undefined) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };
    const displayName = (name) => {
      if (!name || !name.includes(',')) return name || '';
      const [last, first] = name.split(',');
      return `${first.trim()} ${last.trim()}`.trim();
    };
    const initials = (name) => {
      const disp = displayName(name);
      return disp.split(/\s+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
    };
    const age = (birthdate) => {
      if (!birthdate) return '—';
      const b = new Date(birthdate);
      if (isNaN(b)) return '—';
      const diff = Date.now() - b.getTime();
      return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
    };
    const fmtDate = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
    };

    const chaperone = getActiveTeacher();
    const centerName = organization?.name || 'Childcare Center';
    const centerPhone = organization?.phone || '';
    const centerAddress = organization?.address || '';
    const kids = selectedChildrenData.slice().sort((a, b) =>
      (displayName(a.name) || '').localeCompare(displayName(b.name) || '')
    );
    const total = kids.length;
    const today = new Date().toLocaleDateString();

    const rosterRows = kids.map((c, i) => {
      const hasAllergies = !!(c.allergies && String(c.allergies).trim() && String(c.allergies).toLowerCase() !== 'none');
      const hasMeds = !!(c.medicationSpecialConditions && String(c.medicationSpecialConditions).trim());
      const parentPhone = (c.pickupPersons && c.pickupPersons[0] && c.pickupPersons[0].phone) || '';
      return `
        <tr>
          <td class="num">${i + 1}</td>
          <td>${esc(displayName(c.name))}</td>
          <td>${esc(parentPhone)}</td>
          <td class="flags">${hasAllergies ? '🚨' : ''} ${hasMeds ? '💊' : ''}</td>
        </tr>`;
    }).join('');

    const coverPage = `
      <section class="page cover">
        <h1>FIELD TRIP INFORMATION PACKET</h1>
        <div class="center-info">
          <div class="center-name">${esc(centerName)}</div>
          ${centerPhone ? `<div>${esc(centerPhone)}</div>` : ''}
          ${centerAddress ? `<div>${esc(centerAddress)}</div>` : ''}
        </div>
        <div class="trip-fields">
          <div class="field"><span>Trip Date:</span><span class="line"></span></div>
          <div class="field"><span>Destination:</span><span class="line"></span></div>
          <div class="field"><span>Departure Time:</span><span class="line"></span></div>
          <div class="field"><span>Return Time:</span><span class="line"></span></div>
          <div class="field"><span>Chaperone(s):</span><span class="line">${esc(chaperone)}</span></div>
          <div class="field"><span>Additional Chaperone:</span><span class="line"></span></div>
          <div class="field"><span>Total Children:</span><span class="line strong">${total}</span></div>
        </div>
        <h2>Quick Roster</h2>
        <table class="roster">
          <thead><tr><th>#</th><th>Child</th><th>Parent Phone</th><th>Flags</th></tr></thead>
          <tbody>${rosterRows}</tbody>
        </table>
        <div class="legend">
          Legend: 🚨 has allergies · 💊 has medications · ⚠️ missing critical info · ✅ consent current
        </div>
        <div class="signatures">
          <div class="sig-line"><span>Lead Chaperone Signature</span><span class="date">Date: __________</span></div>
          <div class="sig-line"><span>Assistant Chaperone Signature</span><span class="date">Date: __________</span></div>
        </div>
      </section>`;

    const childPages = kids.map((c, idx) => {
      const disp = displayName(c.name);
      const cat = c.fullTimeSubcategory || c.category || '';
      const allergiesText = (c.allergies || '').trim();
      const medsText = (c.medicationSpecialConditions || '').trim();
      const medicalText = (c.medicalEmergencyInfo || c.specialNeedsNotes || '').trim();
      const emergencyDate = c.forms?.emergencyContact?.signedDate;
      let consentBanner = '';
      if (!emergencyDate) {
        consentBanner = `<div class="banner red">🚨 No Emergency Consent on file — verify authorization before departure</div>`;
      } else {
        const signed = new Date(emergencyDate);
        const monthsOld = (Date.now() - signed.getTime()) / (30.44 * 24 * 3600 * 1000);
        if (monthsOld > 12) {
          consentBanner = `<div class="banner amber">⚠️ Emergency Consent signed ${Math.round(monthsOld)} months ago — consider updating</div>`;
        }
      }
      const phys = c.physician || {};
      const ins = c.healthInsurance || {};
      const pickup = c.pickupPersons || [];
      const parent1 = pickup[0] || {};
      const parent2 = pickup[1] || {};
      const guardians = pickup.slice(2).filter(p => p.name);
      const ec = (c.emergencyContacts || []).filter(e => e.name);
      const photoBlock = c.photoUrl
        ? `<div class="photo"><img src="${esc(c.photoUrl)}" alt=""/></div>`
        : `<div class="photo placeholder">${esc(initials(c.name))}</div>`;

      const guardianRows = guardians.map(g => `
        <tr><td>${esc(g.relationship || 'Guardian')}</td><td>${esc(g.name)}</td><td>${esc(g.phone || '')}</td></tr>
      `).join('');
      const ecRows = ec.map(e => `
        <tr><td>Emergency</td><td>${esc(e.name)}</td><td>${esc(e.phone || '')}</td></tr>
      `).join('');

      return `
        <section class="page child">
          <header class="child-header">
            <div class="left">${esc(centerName)} · Trip Date: __________ · Chaperone: ${esc(chaperone)}</div>
            <div class="right">EXCURSION INFO SHEET · Page ${idx + 2} of ${total + 1}</div>
          </header>
          ${consentBanner}
          <div class="child-top">
            ${photoBlock}
            <div class="name-block">
              <h2>${esc(disp)}</h2>
              <div class="meta">DOB ${esc(fmtDate(c.birthdate))} · Age ${age(c.birthdate)} · ${esc(cat)}</div>
              ${c.startDate ? `<div class="meta small">Enrolled: ${esc(fmtDate(c.startDate))}</div>` : ''}
            </div>
          </div>
          <div class="medical">
            <div class="row"><span class="icon">🚨</span><span class="lbl">ALLERGIES</span>
              <span class="val ${allergiesText ? '' : 'muted'}">${esc(allergiesText || 'None on file')}</span></div>
            <div class="row"><span class="icon">💊</span><span class="lbl">MEDICATIONS</span>
              <span class="val ${medsText ? '' : 'muted'}">${esc(medsText || 'None on file')}</span></div>
            <div class="row"><span class="icon">📋</span><span class="lbl">MEDICAL NOTES</span>
              <span class="val ${medicalText ? '' : 'muted'}">${esc(medicalText || 'None on file')}</span></div>
          </div>
          <table class="contacts">
            <tr><td class="ltype">Parent 1</td><td>${esc(parent1.relationship || (parent1.name ? 'Parent' : ''))}</td>
              <td>${esc(parent1.name || '')}</td><td>${esc(parent1.phone || '')}</td></tr>
            <tr><td class="ltype">Parent 2</td><td>${esc(parent2.relationship || (parent2.name ? 'Parent' : ''))}</td>
              <td>${esc(parent2.name || '')}</td><td>${esc(parent2.phone || '')}</td></tr>
            ${guardianRows}
            ${ecRows}
            ${(!parent1.name && !parent2.name) ? '<tr><td colspan="4" class="warn">⚠️ No parent contact on file — cannot depart</td></tr>' : ''}
          </table>
          <div class="footer-band">
            <div class="phys">
              <div class="hdr">PHYSICIAN</div>
              ${phys.name
                ? `<div>${esc(phys.name)} · ${esc(phys.phone || '')}</div>
                   ${phys.address ? `<div class="addr">${esc(phys.address)}</div>` : ''}`
                : '<div class="warn">⚠️ Not on file — obtain from parent before departure</div>'}
            </div>
            <div class="ins">
              <div class="hdr">INSURANCE</div>
              ${ins.provider
                ? `<div>${esc(ins.provider)}${ins.policyNumber ? ` · Policy ${esc(ins.policyNumber)}` : ''}</div>`
                : '<div class="warn">⚠️ Not on file</div>'}
            </div>
          </div>
          <div class="consent-footer">
            ${emergencyDate
              ? `Emergency Consent on file — signed ${esc(fmtDate(emergencyDate))} ✅`
              : ''}
          </div>
        </section>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><title>Field Trip Info Packet — ${esc(today)}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; color: #1e293b; margin: 0; }
  .page { page-break-after: always; page-break-inside: avoid; min-height: 9.5in; padding: 0.1in 0; }
  .page:last-child { page-break-after: auto; }

  /* Cover page */
  .cover h1 { font-size: 22px; text-align: center; letter-spacing: 1px; margin: 0 0 12px; color: #0f172a; }
  .cover .center-info { text-align: center; margin-bottom: 18px; color: #334155; }
  .cover .center-name { font-weight: 700; font-size: 16px; color: #0f172a; }
  .cover .trip-fields { margin: 12px 0 18px; }
  .cover .field { display: flex; align-items: baseline; gap: 8px; padding: 6px 0; font-size: 14px; }
  .cover .field span:first-child { min-width: 160px; font-weight: 600; color: #475569; }
  .cover .field .line { flex: 1; border-bottom: 1px solid #94a3b8; min-height: 1em; padding-left: 4px; }
  .cover .field .line.strong { font-weight: 700; border-bottom: none; }
  .cover h2 { font-size: 15px; margin: 18px 0 6px; color: #0f172a; border-bottom: 2px solid #0ea5a4; padding-bottom: 3px; }
  table.roster { width: 100%; border-collapse: collapse; font-size: 12px; }
  table.roster th, table.roster td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; }
  table.roster th { background: #f1f5f9; }
  table.roster td.num { width: 30px; text-align: center; }
  table.roster td.flags { width: 70px; text-align: center; }
  .legend { margin: 10px 0 18px; font-size: 11px; color: #64748b; font-style: italic; }
  .signatures { margin-top: 22px; }
  .sig-line { display: flex; justify-content: space-between; border-bottom: 1px solid #0f172a; padding: 22px 0 2px; font-size: 11px; color: #64748b; }
  .sig-line .date { min-width: 120px; text-align: right; }

  /* Child page */
  .child-header { display: flex; justify-content: space-between; font-size: 10px; color: #64748b; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin-bottom: 8px; }
  .banner { padding: 6px 10px; border-radius: 4px; font-weight: 600; font-size: 12px; margin-bottom: 8px; }
  .banner.red { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
  .banner.amber { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
  .child-top { display: flex; gap: 14px; align-items: center; padding: 8px 0 12px; border-bottom: 2px solid #0ea5a4; }
  .photo { width: 90px; height: 90px; border-radius: 8px; overflow: hidden; flex-shrink: 0; border: 1px solid #cbd5e1; }
  .photo img { width: 100%; height: 100%; object-fit: cover; }
  .photo.placeholder { background: #e2e8f0; color: #475569; font-weight: 700; font-size: 32px; display: flex; align-items: center; justify-content: center; }
  .name-block h2 { margin: 0 0 4px; font-size: 22px; color: #0f172a; }
  .name-block .meta { font-size: 13px; color: #475569; }
  .name-block .meta.small { font-size: 11px; margin-top: 2px; }
  .medical { margin: 10px 0; border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 10px; background: #fafafa; }
  .medical .row { display: flex; gap: 8px; padding: 3px 0; font-size: 12px; border-bottom: 1px dashed #e2e8f0; }
  .medical .row:last-child { border-bottom: none; }
  .medical .icon { width: 20px; flex-shrink: 0; }
  .medical .lbl { width: 130px; font-weight: 700; color: #475569; font-size: 11px; letter-spacing: 0.3px; }
  .medical .val { flex: 1; }
  .medical .val.muted { color: #94a3b8; font-style: italic; }
  table.contacts { width: 100%; border-collapse: collapse; font-size: 12px; margin: 8px 0; }
  table.contacts td { border: 1px solid #e2e8f0; padding: 5px 8px; }
  table.contacts td.ltype { width: 80px; font-weight: 700; color: #475569; font-size: 11px; letter-spacing: 0.3px; background: #f8fafc; }
  table.contacts td.warn { color: #991b1b; font-weight: 700; text-align: center; background: #fee2e2; }
  .footer-band { display: flex; gap: 12px; margin: 8px 0; }
  .footer-band .phys, .footer-band .ins { flex: 1; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px; font-size: 12px; }
  .footer-band .hdr { font-size: 10px; letter-spacing: 0.5px; color: #64748b; font-weight: 700; margin-bottom: 4px; }
  .footer-band .addr { font-size: 11px; color: #64748b; margin-top: 2px; }
  .footer-band .warn { color: #92400e; font-style: italic; font-size: 11px; }
  .consent-footer { text-align: center; font-size: 11px; color: #059669; padding-top: 8px; border-top: 1px dashed #e2e8f0; margin-top: 6px; }
</style></head><body>
${coverPage}
${childPages}
</body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 700);
  };

  // Render a single tag
  const renderTag = (child, index) => {
    const borderColor = BORDER_COLORS[index % BORDER_COLORS.length];
    const teacher = getTeacherForChild(child.id || child._id);
    const schoolName = organization?.name || "Childcare Center";
    const schoolPhone = organization?.phone || "";

    // Create rainbow text for "FIELD TRIP"
    const fieldTripText = "FIELD TRIP".split('').map((char, i) => (
      `<span style="color: ${RAINBOW_COLORS[i % RAINBOW_COLORS.length]}">${char}</span>`
    )).join('');

    return (
      <div key={child.id || child._id} className="tag" style={{ border: `4px solid ${borderColor}` }}>
        <div className="tag-inner">
          <div className="hole-punch"></div>
          <img src="/simple-sign-in-logo.png" alt="Simple Sign In Logo" className="logo" onError={(e) => e.target.style.display = 'none'} />
          <div className="header-text">I am on a</div>
          <div className="field-trip-text" dangerouslySetInnerHTML={{ __html: fieldTripText }}></div>
          
          <div className="field-label">NAME:</div>
          <div className="field-value">{child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}</div>
          
          <div className="field-label">TEACHER:</div>
          <div className="field-value">{teacher}</div>
          
          <div className="school-section">
            <div className="field-label">SCHOOL:</div>
            <div className="field-value">{schoolName}</div>
            
            {schoolPhone && (
              <>
                <div className="field-label">SCHOOL PHONE:</div>
                <div className="field-value">{schoolPhone}</div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppShell pageTitle={language === 'es' ? "Etiquetas de Excursión" : "Field Trip Tags"} breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.fieldTripTags}
        title="Field Trip Tags"
        subtitle="Create printable name tags for field trips and outings"
        altText="Field trip tags preparation"
      />
      
      <div className="relative z-10 p-4 md:p-6 space-y-6">
        {/* Controls Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {language === 'es' ? "Generar Etiquetas de Excursión" : "Generate Field Trip Tags"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filter and Settings Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Category Filter */}
              <div className="space-y-2">
                <Label>{language === 'es' ? "Filtrar por Categoría" : "Filter by Category"}</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger data-testid="category-filter">
                    <SelectValue placeholder={language === 'es' ? "Todas las Categorías" : "All Categories"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? "Todas las Categorías" : "All Categories"}</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{formatCategory(cat)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Teacher Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{language === 'es' ? "Maestro/a" : "Teacher"}</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseCustomTeacher(!useCustomTeacher)}
                    className="text-xs h-6 px-2"
                    data-testid="toggle-custom-teacher"
                  >
                    <Edit3 className="w-3 h-3 mr-1" />
                    {useCustomTeacher 
                      ? (language === 'es' ? "Usar Lista" : "Use List")
                      : (language === 'es' ? "Escribir Nombre" : "Type Name")
                    }
                  </Button>
                </div>
                
                {useCustomTeacher ? (
                  <Input
                    type="text"
                    placeholder={language === 'es' ? "Nombre del acompañante..." : "Enter chaperone name..."}
                    value={customTeacher}
                    onChange={(e) => setCustomTeacher(e.target.value)}
                    data-testid="custom-teacher-input"
                  />
                ) : (
                  <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                    <SelectTrigger data-testid="teacher-select">
                      <SelectValue placeholder={language === 'es' ? "Seleccionar Maestro" : "Select Teacher"} />
                    </SelectTrigger>
                    <SelectContent>
                      {user?.name && (
                        <SelectItem value={user.name}>{user.name} (Owner)</SelectItem>
                      )}
                      {staffMembers.filter(name => name !== user?.name).map(name => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Apply to All Button */}
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  onClick={handleApplyTeacherToAll}
                  variant="outline"
                  className="w-full"
                  disabled={selectedChildren.length === 0}
                  data-testid="apply-all-btn"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  {language === 'es' ? "Aplicar a Todos" : "Apply to All"}
                </Button>
              </div>

              {/* Orientation */}
              <div className="space-y-2">
                <Label>{language === 'es' ? "Orientación" : "Orientation"}</Label>
                <Select value={orientation} onValueChange={setOrientation}>
                  <SelectTrigger data-testid="orientation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">{language === 'es' ? "Vertical (8 por página)" : "Portrait (8 per page)"}</SelectItem>
                    <SelectItem value="landscape">{language === 'es' ? "Horizontal (6 por página)" : "Landscape (6 per page)"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Children Selection */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">
                  {language === 'es' ? "Seleccionar Niños" : "Select Children"} 
                  <span className="text-sm text-gray-500 ml-2">
                    ({selectedChildren.length} {language === 'es' ? "seleccionados" : "selected"})
                  </span>
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAll}
                  data-testid="select-all-btn"
                >
                  {selectedChildren.length === filteredChildren.length ? (
                    <><Square className="w-4 h-4 mr-2" /> {language === 'es' ? "Deseleccionar Todos" : "Deselect All"}</>
                  ) : (
                    <><CheckSquare className="w-4 h-4 mr-2" /> {language === 'es' ? "Seleccionar Todos" : "Select All"}</>
                  )}
                </Button>
              </div>
              
              {loading ? (
                <div className="text-center py-4 text-gray-500">
                  {language === 'es' ? "Cargando..." : "Loading..."}
                </div>
              ) : filteredChildren.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  {language === 'es' ? "No hay niños en esta categoría" : "No children in this category"}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                  {filteredChildren.map(child => (
                    <label
                      key={child.id || child._id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                        selectedChildren.includes(child.id || child._id) ? 'bg-blue-50 border border-blue-200' : ''
                      }`}
                    >
                      <Checkbox
                        checked={selectedChildren.includes(child.id || child._id)}
                        onCheckedChange={() => handleChildSelect(child.id || child._id)}
                        data-testid={`child-checkbox-${child.id || child._id}`}
                      />
                      <span className="text-sm truncate">{child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedChildren([]);
                  setChildTeachers({});
                }}
                data-testid="reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {language === 'es' ? "Reiniciar" : "Reset"}
              </Button>
              <Button
                onClick={handlePrint}
                disabled={selectedChildren.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="print-tags-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                {language === 'es' ? "Imprimir Etiquetas" : "Print Tags"} ({selectedChildren.length})
              </Button>
              <Button
                onClick={handlePrintExcursionSheets}
                disabled={selectedChildren.length === 0}
                variant="outline"
                className="border-teal-600 text-teal-700 hover:bg-teal-50"
                title="One-page info sheet per child — photo, allergies, physician, insurance, parents & emergency contacts"
                data-testid="print-excursion-sheets-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                {language === 'es' ? "Imprimir Hojas de Información" : "Print Info Sheets"} ({selectedChildren.length})
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Section */}
        {selectedChildrenData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'es' ? "Vista Previa" : "Preview"} 
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({orientation === 'portrait' ? '8' : '6'} {language === 'es' ? "por página" : "per page"})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Cut guide instruction */}
              <div className="text-center text-gray-500 text-sm mb-2 pb-2 border-b border-dashed border-gray-400">
                ✂️ Cut along dashed lines ✂️
              </div>
              <div 
                className="bg-gray-100 p-4 rounded-lg overflow-auto"
                style={{ maxHeight: '600px' }}
              >
                <div 
                  ref={printRef}
                  className="tags-container"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${orientation === 'portrait' ? 4 : 3}, 162px)`,
                    gap: '0',
                    justifyContent: 'center',
                    background: 'white',
                    padding: '16px'
                  }}
                >
                  {selectedChildrenData.map((child, index) => {
                    const borderColor = BORDER_COLORS[index % BORDER_COLORS.length];
                    const teacher = getTeacherForChild(child.id || child._id);
                    const schoolName = organization?.name || "Childcare Center";
                    const schoolPhone = organization?.phone || "";
                    const cols = orientation === 'portrait' ? 4 : 3;
                    const isLastInRow = (index + 1) % cols === 0;

                    return (
                      <div 
                        key={child.id || child._id}
                        className="tag-wrapper"
                        style={{
                          width: '162px',
                          height: '252px',
                          position: 'relative',
                          borderRight: isLastInRow ? 'none' : '1px dashed #999',
                          borderBottom: '1px dashed #999'
                        }}
                      >
                        <div 
                          className="tag"
                          style={{
                            width: '100%',
                            height: '100%',
                            border: `4px solid ${borderColor}`,
                            borderRadius: '12px',
                            padding: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            background: 'white',
                            fontFamily: "'Comic Sans MS', 'Chalkboard', sans-serif",
                            boxSizing: 'border-box'
                          }}
                        >
                        {/* Hole punch */}
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          border: '3px solid #888',
                          marginBottom: '4px',
                          background: 'white'
                        }}></div>
                        
                        {/* Logo */}
                        <img 
                          src="/simple-sign-in-logo.png" 
                          alt="Logo" 
                          style={{ width: '36px', height: 'auto', marginBottom: '2px' }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        
                        {/* Header */}
                        <div style={{ fontSize: '10px', color: '#333', marginBottom: '2px' }}>
                          I am on a
                        </div>
                        
                        {/* FIELD TRIP Rainbow Text */}
                        <div style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '6px' }}>
                          {"FIELD TRIP".split('').map((char, i) => (
                            <span key={i} style={{ color: RAINBOW_COLORS[i % RAINBOW_COLORS.length] }}>{char}</span>
                          ))}
                        </div>
                        
                        {/* Name */}
                        <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#333', marginTop: '4px' }}>NAME:</div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: '#222', 
                          borderBottom: '1px solid #999',
                          paddingBottom: '2px',
                          marginBottom: '4px',
                          width: '90%',
                          textAlign: 'center'
                        }}>
                          {child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}
                        </div>
                        
                        {/* Teacher */}
                        <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#333', marginTop: '2px' }}>TEACHER:</div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: '#222', 
                          borderBottom: '1px solid #999',
                          paddingBottom: '2px',
                          marginBottom: '4px',
                          width: '90%',
                          textAlign: 'center'
                        }}>
                          {teacher}
                        </div>
                        
                        {/* School Section */}
                        <div style={{ marginTop: 'auto', width: '100%', textAlign: 'center' }}>
                          <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#333' }}>SCHOOL:</div>
                          <div style={{ 
                            fontSize: '9px', 
                            color: '#222', 
                            borderBottom: '1px solid #999',
                            paddingBottom: '2px',
                            marginBottom: '4px',
                            width: '90%',
                            margin: '0 auto 4px auto'
                          }}>
                            {schoolName}
                          </div>
                          
                          {schoolPhone && (
                            <>
                              <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#333' }}>SCHOOL PHONE:</div>
                              <div style={{ 
                                fontSize: '9px', 
                                color: '#222', 
                                borderBottom: '1px solid #999',
                                paddingBottom: '2px',
                                width: '90%',
                                margin: '0 auto'
                              }}>
                                {schoolPhone}
                              </div>
                            </>
                          )}
                        </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
};

export default FieldTripTagsPage;
